// import { useAuth } from "../providers";
import { isNativePlatform } from '@lib/platform';

/**
 * AIDEV-NOTE: getApiConfig function has been removed.
 * Migration: Use the exported apiConfig object directly for API configuration.
 * JWT authentication is now handled by the API client interceptor.
 * If you previously used getApiConfig, replace calls with direct usage of apiConfig.
 */

/**
 * API Configuration
 * Centralized configuration for API endpoints and settings
 */

/**
 * Get the API base URL from environment variables
 * Falls back to localhost for development if not set
 *
 *
 */

// JWT ID token authentication is handled by the API client interceptor

export const getApiBaseUrl = (): string => {
  // Native: must use absolute backend URL (no Vite proxy or Cloudflare Pages proxy).
  // Checked first because .env.local may contain localhost URLs for web dev
  // that don't work on a physical device.
  if (isNativePlatform()) {
    // AIDEV-NOTE: Default to deployed dev API. For local development, set
    // VITE_NATIVE_API_BASE_URL=http://<your-mac-lan-ip>:8808/ in .env.local
    const nativeBaseUrl =
      import.meta.env.VITE_NATIVE_API_BASE_URL || 'https://dev-api.meetball.fun/';
    return nativeBaseUrl.endsWith('/') ? nativeBaseUrl : `${nativeBaseUrl}/`;
  }

  // Explicit override for web (set in .env, .env.local, or build-time)
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  if (baseUrl) {
    return baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
  }

  // Web development: use relative URL to utilize Vite proxy
  // This makes requests go through localhost:5173 which Vite proxies to the backend
  if (import.meta.env.DEV) {
    return '/';
  }

  // Web production without VITE_API_BASE_URL (Cloudflare Pages proxies /api/*)
  return '/';
};

/**
 * Get the Google Places API key for the current platform.
 * Native builds use a separate key with API-only restriction (no HTTP referrer),
 * since capacitor:// and https://localhost origins can't be added as referrers.
 */
export const getGooglePlacesApiKey = (): string | undefined => {
  if (isNativePlatform()) {
    return (
      import.meta.env.VITE_NATIVE_GOOGLE_PLACES_API_KEY ||
      import.meta.env.VITE_GOOGLE_PLACES_API_KEY
    );
  }
  return import.meta.env.VITE_GOOGLE_PLACES_API_KEY;
};

/**
 * API configuration object
 */
export const apiConfig = {
  baseURL: getApiBaseUrl(),
  timeout: 30000, // 30 seconds - increased for slower connections
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    // JWT ID token authentication is handled by the API client interceptor
  },
} as const;

// JWT ID token authentication is handled by the API client interceptor
// This function is no longer needed as authentication is centralized in the interceptor

/**
 * Environment-specific configuration
 */
export const isDevelopment = import.meta.env.DEV;
export const isProduction = import.meta.env.PROD;

/**
 * Debug logging for API requests (only in development)
 */
export const shouldLogApiRequests = isDevelopment;
